import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import api from '../../services/api';
import { Team } from '../../types';
import './TeamSearch.css';

// Icons as components
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const SportsSoccerIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
  </svg>
);

const LocationOnIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const CalendarTodayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const EmojiEventsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="6" />
    <path d="M5 14h14M8 22h8" />
    <path d="M12 14v8" />
  </svg>
);

const StadiumIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v20M2 12h20M4 4l16 16M20 4L4 20" />
    <circle cx="12" cy="12" r="4" />
  </svg>
);

const VerifiedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

// Types
interface TeamStats {
  totalTeams: number;
  totalLeagues: number;
  totalCountries: number;
}

// Stats Summary Component
const StatsSummary: React.FC<{ teams: Team[] }> = memo(({ teams }) => {
  const stats = useMemo<TeamStats>(() => {
    const uniqueLeagues = new Set(teams.map(t => t.league).filter(Boolean));
    const uniqueCountries = new Set(teams.map(t => t.country).filter(Boolean));
    
    return {
      totalTeams: teams.length,
      totalLeagues: uniqueLeagues.size,
      totalCountries: uniqueCountries.size
    };
  }, [teams]);

  if (teams.length === 0) return null;

  return (
    <div className="team-stats-summary">
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label">Total Teams</span>
          <span className="stat-value">{stats.totalTeams}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Leagues</span>
          <span className="stat-value">{stats.totalLeagues}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Countries</span>
          <span className="stat-value">{stats.totalCountries}</span>
        </div>
      </div>
    </div>
  );
});

StatsSummary.displayName = 'StatsSummary';

// Team Card Component
const TeamCard: React.FC<{ team: Team; index: number }> = memo(({ team, index }) => {
  const [imageError, setImageError] = useState(false);
  
  const getCountryFlag = (country: string): string => {
    if (!country || country === 'Unknown') return '🏳️';
    
    const flags: { [key: string]: string } = {
      'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
      'Spain': '🇪🇸',
      'Italy': '🇮🇹',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Netherlands': '🇳🇱',
      'Portugal': '🇵🇹',
      'Brazil': '🇧🇷',
      'Argentina': '🇦🇷',
      'USA': '🇺🇸',
    };
    return flags[country] || '🏳️';
  };

  const getLeagueIcon = (league: string): string => {
    if (!league || league === 'Unknown') return '⚽';
    if (league.includes('Premier')) return '🏆';
    if (league.includes('La Liga')) return '👑';
    if (league.includes('Bundesliga')) return '🍺';
    if (league.includes('Serie A')) return '⚽';
    if (league.includes('Ligue 1')) return '🥖';
    if (league.includes('Champions')) return '🌟';
    return '⚽';
  };

  const getStadiumDisplay = (stadium?: string): string => {
    if (!stadium || stadium === 'Unknown') return 'Stadium information unavailable';
    return stadium;
  };

  const getFoundedDisplay = (founded?: number): string => {
    if (!founded) return 'Founded: Unknown';
    return `Founded: ${founded} ${founded < 1900 ? '✨' : ''}`;
  };

  return (
    <div className="team-card" style={{ animationDelay: `${index * 0.05}s` }}>
      <div className="team-card-header">
        <div className="team-header-content">
          {team.logo_url && !imageError ? (
            <img 
              src={team.logo_url} 
              alt={team.name}
              className="team-avatar"
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : (
            <div className="team-avatar placeholder">
              <SportsSoccerIcon />
            </div>
          )}
          <div className="team-title">
            <h3 className="team-name">
              {team.name}
              {team.founded && team.founded < 1900 && (
                <span className="historic-badge" title="Historic Club">🏛️</span>
              )}
            </h3>
            <span className="team-league" title={team.league || 'League unknown'}>
              {getLeagueIcon(team.league)} {team.league || 'League Unknown'}
            </span>
          </div>
        </div>
      </div>
      
      <div className="team-card-content">
        <div className="team-info-row">
          <LocationOnIcon />
          <span className="team-info-text" title={team.country || 'Country unknown'}>
            {getCountryFlag(team.country)} {team.country || 'Country Unknown'}
          </span>
        </div>
        
        <div className="team-info-row">
          <StadiumIcon />
          <span className="team-info-text" title={getStadiumDisplay(team.stadium)}>
            {team.stadium && team.stadium !== 'Unknown' 
              ? (team.stadium.length > 25 ? `${team.stadium.substring(0, 25)}...` : team.stadium)
              : '—'}
          </span>
        </div>
        
        <div className="team-info-row">
          <CalendarTodayIcon />
          <span className="team-info-text" title={getFoundedDisplay(team.founded)}>
            {team.founded ? `Founded: ${team.founded} ${team.founded < 1900 ? '✨' : ''}` : '—'}
          </span>
        </div>

        {/* Data Source Indicator */}
        <div className="team-data-source">
          <VerifiedIcon />
          <span className="data-source-text">
            {team.id ? 'Verified Data' : 'Historical Record'}
          </span>
        </div>
      </div>
    </div>
  );
});

TeamCard.displayName = 'TeamCard';

// Main Component
const TeamSearch: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [recentTeams, setRecentTeams] = useState<Team[]>([]);
  const [popularTeams, setPopularTeams] = useState<Team[]>([]);
  const [dataSource, setDataSource] = useState<'api' | 'database' | 'sample'>('api');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<string>('');
  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());

  // Load search history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('teamSearchHistory');
    if (saved) {
      try {
        setSearchHistory(JSON.parse(saved).slice(0, 5));
      } catch (e) {
        console.error('Failed to parse search history');
      }
    }
  }, []);

  const searchTeams = useCallback(async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    const startTime = performance.now();
    
    try {
      const response = await api.get(`/teams/search/?q=${encodeURIComponent(searchQuery)}`);
      const endTime = performance.now();
      
      console.log(`🔍 Team search completed in ${(endTime - startTime).toFixed(0)}ms`);
      console.log('Search results:', response.data);
      
      // Handle different response formats
      let searchResults: Team[] = [];
      
      if (Array.isArray(response.data)) {
        searchResults = response.data;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        searchResults = response.data.results;
      } else if (response.data.teams && Array.isArray(response.data.teams)) {
        searchResults = response.data.teams;
      } else {
        console.warn('Unexpected search response format:', response.data);
        searchResults = [];
      }
      
      setTeams(searchResults);
      setDataSource(searchResults.length > 0 ? 'api' : 'database');
      setLastUpdated(new Date());
      
      // Save to search history
      if (searchResults.length > 0) {
        const newHistory = [searchQuery, ...searchHistory.filter(s => s !== searchQuery)].slice(0, 5);
        setSearchHistory(newHistory);
        localStorage.setItem('teamSearchHistory', JSON.stringify(newHistory));
      }
      
    } catch (error) {
      console.error('Team search failed:', error);
      setTeams([]);
      setDataSource('sample');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchHistory]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const debounceTimer = setTimeout(() => {
        searchTeams();
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      setTeams([]);
    }
  }, [searchQuery, searchTeams]);

  const fetchInitialData = useCallback(async () => {
    try {
      const response = await api.get('/teams/');
      console.log('Initial teams data:', response.data);
      
      // Handle different response formats
      let allTeams: Team[] = [];
      
      if (Array.isArray(response.data)) {
        allTeams = response.data;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        allTeams = response.data.results;
      } else if (response.data.teams && Array.isArray(response.data.teams)) {
        allTeams = response.data.teams;
      } else {
        console.warn('Unexpected teams response format:', response.data);
        throw new Error('Invalid response format');
      }
      
      if (allTeams.length === 0) {
        console.log('No teams found in database');
        setRecentTeams([]);
        setPopularTeams([]);
        return;
      }
      
      // Sort by different criteria
      const byRecent = [...allTeams].sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });
      
      // For popular teams, sort by ID or random for now
      const byPopular = [...allTeams].sort((a, b) => b.id - a.id);
      
      setRecentTeams(byRecent.slice(0, 6));
      setPopularTeams(byPopular.slice(0, 6));
      setDataSource('api');
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      setDataSource('sample');
      setRecentTeams([]);
      setPopularTeams([]);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const filteredTeams = useMemo(() => {
    if (!selectedLeague) return teams;
    return teams.filter(team => team.league === selectedLeague);
  }, [teams, selectedLeague]);

  const uniqueLeagues = useMemo(() => {
    const leagues = new Set(teams.map(t => t.league).filter(l => l && l !== 'Unknown'));
    return Array.from(leagues);
  }, [teams]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setTeams([]);
    setSelectedLeague('');
  }, []);

  const handleHistoryClick = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleImageError = (teamId: number) => {
    setBrokenImages(prev => new Set(prev).add(teamId));
  };

  const getDataSourceIcon = () => {
    switch (dataSource) {
      case 'api': return '🌐';
      case 'database': return '💾';
      case 'sample': return '⚠️';
      default: return '❓';
    }
  };

  const getDataSourceText = () => {
    switch (dataSource) {
      case 'api': return 'Live API Data';
      case 'database': return 'Cached Database';
      case 'sample': return 'Sample Data';
      default: return 'Unknown Source';
    }
  };

  // Helper function for date formatting
  const formatTime = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className="team-search-container">
      <div className="team-search-paper">
        <div className="header-section">
          <h1 className="team-search-title">
            🔍 Team Search
          </h1>
          
          {/* Data Source Indicator */}
          <div className={`data-source-indicator ${dataSource}`} title={`Data source: ${getDataSourceText()}`}>
            <span className="source-icon">{getDataSourceIcon()}</span>
            <span className="source-text">{getDataSourceText()}</span>
            {lastUpdated && (
              <span className="last-updated">
                Updated: {formatTime(lastUpdated)}
              </span>
            )}
          </div>
        </div>

        {/* Stats Summary - Only show when we have data */}
        {!searchQuery && (recentTeams.length > 0 || popularTeams.length > 0) && (
          <StatsSummary teams={[...recentTeams, ...popularTeams]} />
        )}

        {/* Data Collection Banner - Show when no teams in database */}
        {!searchQuery && recentTeams.length === 0 && popularTeams.length === 0 && dataSource === 'api' && (
          <div className="data-collection-banner">
            <InfoIcon />
            <span>
              📊 Collecting team data from football-data.org API. Teams will appear here once collected.
            </span>
          </div>
        )}

        {/* Search Bar */}
        <div className="search-bar-container">
          <div className="search-input-wrapper">
            <span className="search-icon">
              <SearchIcon />
            </span>
            <input
              type="text"
              className="search-input"
              placeholder="Search for a team (e.g., Manchester United, Barcelona, Real Madrid)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              spellCheck="false"
            />
            {loading && (
              <span className="search-spinner">
                <div className="spinner"></div>
              </span>
            )}
            {searchQuery && !loading && (
              <button className="clear-search" onClick={handleClearSearch} title="Clear search">
                ✕
              </button>
            )}
          </div>

          {/* Search History */}
          {searchHistory.length > 0 && !searchQuery && (
            <div className="search-history">
              <span className="history-label">Recent searches:</span>
              {searchHistory.map((query, index) => (
                <button
                  key={index}
                  className="history-chip"
                  onClick={() => handleHistoryClick(query)}
                >
                  {query}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* League Filter (shown when results exist) */}
        {teams.length > 0 && uniqueLeagues.length > 1 && (
          <div className="league-filters">
            <span className="filter-label">Filter by league:</span>
            <button
              className={`league-filter-btn ${!selectedLeague ? 'active' : ''}`}
              onClick={() => setSelectedLeague('')}
            >
              All
            </button>
            {uniqueLeagues.map(league => (
              <button
                key={league}
                className={`league-filter-btn ${selectedLeague === league ? 'active' : ''}`}
                onClick={() => setSelectedLeague(league)}
              >
                {league}
              </button>
            ))}
          </div>
        )}

        {/* Search Results */}
        {filteredTeams.length > 0 ? (
          <>
            <div className="section-header">
              <h2 className="section-title">
                Search Results {selectedLeague && `• ${selectedLeague}`}
              </h2>
              <span className="result-count">
                {filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''} found
                {selectedLeague && ` in ${selectedLeague}`}
              </span>
            </div>
            <div className="teams-grid">
              {filteredTeams.map((team, index) => (
                <TeamCard key={team.id} team={team} index={index} />
              ))}
            </div>
          </>
        ) : searchQuery.length >= 2 && !loading ? (
          <div className="no-results">
            <div className="no-results-icon">
              <SearchIcon />
            </div>
            <h3 className="no-results-title">No teams found</h3>
            <p className="no-results-text">
              We couldn't find any teams matching "{searchQuery}"
            </p>
            {dataSource === 'sample' && (
              <p className="no-results-hint">
                ℹ️ You're viewing sample data. Connect to API for live team search.
              </p>
            )}
          </div>
        ) : null}

        {/* Popular Teams */}
        {!searchQuery && popularTeams.length > 0 && (
          <>
            <h2 className="section-title with-margin">
              ⭐ Popular Teams
              <span className="section-subtitle">Most searched teams</span>
            </h2>
            <div className="teams-grid">
              {popularTeams.map((team, index) => (
                <TeamCard key={team.id} team={team} index={index} />
              ))}
            </div>
          </>
        )}

        {/* Recent Teams */}
        {!searchQuery && recentTeams.length > 0 && (
          <>
            <h2 className="section-title with-margin">
              🕒 Recently Added
              <span className="section-subtitle">Newest teams in database</span>
            </h2>
            <div className="teams-grid">
              {recentTeams.map((team, index) => (
                <TeamCard key={team.id} team={team} index={index + popularTeams.length} />
              ))}
            </div>
          </>
        )}

        {/* Data Attribution */}
        <div className="data-attribution">
          <p>
            Team data provided by football-data.org • 
            <span className={`data-quality ${dataSource}`}>
              {getDataSourceIcon()} {getDataSourceText()}
            </span>
          </p>
          {dataSource === 'sample' && (
            <button className="refresh-data-btn" onClick={fetchInitialData}>
              🔄 Refresh Data
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(TeamSearch);